import {
  Badge,
  Button,
  DataTable,
  Switch,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { Eye } from "lucide-react";
import type { FeedListItem } from "@/modules/cf-feed/presentation/api/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";

const STATUS_VARIANT: Record<
  FeedListItem["displayStatus"],
  "default" | "secondary" | "destructive" | "outline"
> = {
  active: "default",
  draft: "secondary",
  closed: "outline",
  inactive: "secondary",
};

type FeedsTableProps = {
  data: FeedListItem[];
  isLoading?: boolean;
  togglingId?: string | null;
  offset: number;
  limit: number;
  total: number;
  onPaginationChange: (offset: number, limit: number) => void;
  onToggle: (feed: FeedListItem, enabled: boolean) => void;
};

export function FeedsTable({
  data,
  isLoading,
  togglingId,
  offset,
  limit,
  total,
  onPaginationChange,
  onToggle,
}: FeedsTableProps) {
  const nav = useNavigate();

  const columns: TanstackReactTable.ColumnDef<FeedListItem>[] = [
    {
      id: "ordering",
      header: "ປິດ/ເປີດ",
      cell: ({ row }) => (
        <Switch
          checked={row.original.orderingEnabled}
          disabled={togglingId === row.original.id}
          onCheckedChange={(checked) =>
            onToggle(row.original, checked === true)
          }
          aria-label={row.original.orderingEnabled ? "ປິດຮັບຄໍາສັ່ງ" : "ເປີດຮັບຄໍາສັ່ງ"}
        />
      ),
      size: 80,
    },
    {
      id: "title",
      header: "ຫົວຂໍ້",
      cell: ({ row }) => {
        const text =
          row.original.messagePreview ??
          row.original.title ??
          row.original.facebookFeedId;
        return (
          <div className="flex max-w-md items-start gap-2">
            {row.original.thumbnailUrl ? (
              <div className="size-10 shrink-0 overflow-hidden rounded border bg-muted">
                <AppImage
                  src={row.original.thumbnailUrl}
                  alt=""
                  className="size-full object-cover"
                />
              </div>
            ) : null}
            <p className="line-clamp-2 text-sm">{text}</p>
          </div>
        );
      },
      size: 280,
    },
    {
      id: "page",
      header: "ເພຈເຊື່ອມຕໍ່",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.pageName}</span>
      ),
      size: 140,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={STATUS_VARIANT[row.original.displayStatus]}>
          {row.original.displayStatusLabel}
        </Badge>
      ),
      size: 120,
    },
    {
      id: "publishedAt",
      header: "ວັນເວລາ",
      cell: ({ row }) =>
        row.original.publishedAt ? (
          <span className="text-muted-foreground text-sm">
            {formatDateTimeLocal(row.original.publishedAt)}
          </span>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        ),
      size: 160,
    },
    {
      id: "actions",
      header: "ຈັດການ",
      cell: ({ row }) => (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            nav({
              to: "/app/feeds/$id",
              params: { id: row.original.id },
              search: { tab: "customers" },
            })
          }
        >
          <Eye className="size-4" />
          ລາຍລະອຽດ
        </Button>
      ),
      size: 120,
    },
  ];

  return (
    <div className="rounded-xl border bg-card">
      <div className="border-b px-4 py-3">
        <h2 className="font-medium text-sm">ລາຍການໂພສ</h2>
      </div>
      <DataTable<FeedListItem, unknown>
        data={data}
        columns={columns}
        isLoading={isLoading}
        noDataMessage="ຍັງບໍ່ມີໂພສ — ເຊື່ອມ Page ແລ້ວກົດ «ດຶງໂພສໃໝ່»"
        offset={offset}
        limit={limit}
        totalCount={total}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      />
    </div>
  );
}
